/**
 * Analytics Service Module
 *
 * This module provides Firebase Analytics integration for the NightOwl NEO Portal.
 * It follows patterns established in the myAir web and iOS applications.
 *
 * @example
 * ```tsx
 * // In your app entry point (index.tsx)
 * import { initializeAnalytics } from 'services/analytics';
 * await initializeAnalytics();
 *
 * // In components
 * import { useAnalytics, useScreenTracking } from 'services/analytics';
 *
 * function MyComponent() {
 *   const { logEvent, logLogin } = useAnalytics();
 *   useScreenTracking({ screenName: 'My Screen' });
 *
 *   return <div>Content</div>;
 * }
 * ```
 */

// Main service
// Firebase initialization
import { getFirebaseAnalytics, initializeFirebase } from 'configs/firebase';

import { analyticsService } from './analyticsService';

export { analyticsService, default as AnalyticsService } from './analyticsService';

// Hooks
export { useAnalytics, useScreenTracking } from './hooks';
export type { UseAnalyticsReturn } from './hooks';

// Types
export { AnalyticsMode, AnalyticsFeature, LoginType, LogoutCause, UserRole } from './types';
export type { UserProperties, AnalyticsParams, ScreenViewEvent, AnalyticsEvent } from './types';

// Event constants
export { ANALYTICS_EVENTS, ANALYTICS_PARAMS, SCREEN_NAMES, REPORT_TYPES } from './events';
export type { AnalyticsEventName, AnalyticsParamName, ScreenName, ReportType } from './events';

/**
 * Initialize the analytics system.
 * This should be called once at application startup, after Firebase is initialized.
 *
 * @example
 * ```tsx
 * // In index.tsx
 * import { initializeAnalytics } from 'services/analytics';
 *
 * async function bootstrap() {
 *   await initializeAnalytics();
 *   // ... rest of app initialization
 * }
 * ```
 */
export async function initializeAnalytics(): Promise<void> {
  await initializeFirebase();
  analyticsService.initialize();
}

export { getFirebaseAnalytics };
